import { ChevronDown, Search, X } from 'lucide-react'
import { clsx } from 'clsx'
import type { PlayerFilter } from '../types'
import type { CompetitionTeam } from '../queries'
import { RangeSlider } from '@/shared/components/ui/RangeSlider'
import styles from './PlayerFilterBar.module.css'

type PlayerFilterBarProps = {
  filter:           PlayerFilter
  teams:            readonly CompetitionTeam[]
  priceMin:         number   // competition-wide cheapest price (slider lower bound)
  priceMax:         number   // competition-wide most expensive price (slider upper bound)
  budgetRemaining:  number   // remaining saldo, drives "Em orçamento"
  onSearch:         (value: string) => void
  onSetPosition:    (pos: PlayerFilter['position']) => void
  onSetTeamCode:    (code: PlayerFilter['teamCode']) => void
  onToggleHideElim: () => void
  onSetPriceRange:  (min: number | null, max: number | null) => void
  onToggleOnBudget: () => void
  onReset:          () => void
}

const formatPrice = (n: number) => `$${n.toFixed(1)}`

// Compact position filter rendered as colored circles matching the pitch position colors.
const POSITION_TABS: Array<{ id: PlayerFilter['position']; short: string; aria: string; cls: string }> = [
  { id: 'ALL', short: 'TODOS', aria: 'Todas as posições', cls: 'posAll' },
  { id: 'GK',  short: 'GR',    aria: 'Goleiros',          cls: 'posGk'  },
  { id: 'DEF', short: 'DEF',   aria: 'Defensores',        cls: 'posDef' },
  { id: 'MID', short: 'MEI',   aria: 'Meio-campistas',    cls: 'posMid' },
  { id: 'FWD', short: 'ATA',   aria: 'Atacantes',         cls: 'posFwd' },
]

export function PlayerFilterBar({
  filter,
  teams,
  priceMin,
  priceMax,
  budgetRemaining,
  onSearch,
  onSetPosition,
  onSetTeamCode,
  onToggleHideElim,
  onSetPriceRange,
  onToggleOnBudget,
  onReset,
}: PlayerFilterBarProps) {
  const hasActiveFilter =
    filter.search !== '' ||
    filter.position !== 'ALL' ||
    filter.teamCode !== '' ||
    filter.hideEliminated ||
    filter.minPrice !== null ||
    filter.maxPrice !== null ||
    filter.onBudgetOnly

  // Slider endpoints come from the pool; only render once the bounds are known and span a range.
  const boundsReady = priceMax > priceMin
  const sliderLo = filter.minPrice ?? priceMin
  const sliderHi = filter.onBudgetOnly
    ? Math.min(budgetRemaining, priceMax)
    : (filter.maxPrice ?? priceMax)

  // Normalize handles back to "no bound" (null) when they sit at the pool extremes.
  const handlePriceChange = (lo: number, hi: number) =>
    onSetPriceRange(lo <= priceMin ? null : lo, hi >= priceMax ? null : hi)

  return (
    <div className={styles.bar}>
      {/* ── Search + team select (one row, half each) ───────────── */}
      <div className={styles.topRow}>
        <div className={styles.searchWrap}>
          <Search size={14} className={styles.searchIcon} aria-hidden="true" />
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Buscar jogador"
            value={filter.search}
            onChange={e => onSearch(e.target.value)}
            aria-label="Buscar jogador"
          />
          {filter.search && (
            <button
              className={styles.clearSearch}
              onClick={() => onSearch('')}
              aria-label="Limpar busca"
            >
              <X size={12} />
            </button>
          )}
        </div>

        <div className={styles.teamSelectWrap}>
          <select
            className={clsx(styles.teamSelect, filter.teamCode && styles.teamSelectActive)}
            value={filter.teamCode}
            onChange={e => onSetTeamCode(e.target.value)}
            aria-label="Filtrar por time"
          >
            <option value="">Todos os times</option>
            {teams.map(team => (
              <option key={team.code} value={team.code}>
                {team.name}
                {team.isEliminated ? ' (eliminado)' : ''}
              </option>
            ))}
          </select>
          <ChevronDown size={14} className={styles.teamSelectChevron} aria-hidden="true" />
        </div>
      </div>

      {/* ── Position circles + price range (one row) ─────────────── */}
      <div className={styles.filterRow}>
        <div className={styles.positionCircles} role="radiogroup" aria-label="Filtrar por posição">
          {POSITION_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="radio"
              aria-checked={filter.position === tab.id}
              aria-label={tab.aria}
              title={tab.aria}
              className={clsx(styles.posCircle, styles[tab.cls], filter.position === tab.id && styles.posCircleActive)}
              onClick={() => onSetPosition(tab.id)}
            >
              {tab.short}
            </button>
          ))}
        </div>

        {boundsReady && (
          <div className={styles.priceInline}>
            <RangeSlider
              min={priceMin}
              max={priceMax}
              step={0.5}
              valueMin={sliderLo}
              valueMax={sliderHi}
              onChange={handlePriceChange}
              formatValue={formatPrice}
              label="Preço"
            />
          </div>
        )}
      </div>

      {/* ── Toggles + reset ─────────────────────────────────────── */}
      <div className={styles.controls}>
        <label className={styles.hideElimLabel}>
          <input
            type="checkbox"
            className={styles.hideElimCheck}
            checked={filter.hideEliminated}
            onChange={onToggleHideElim}
            aria-label="Ocultar eliminados"
          />
          <span>Ocultar eliminados</span>
        </label>

        {boundsReady && (
          <label className={styles.hideElimLabel}>
            <input
              type="checkbox"
              className={styles.hideElimCheck}
              checked={filter.onBudgetOnly}
              onChange={onToggleOnBudget}
              aria-label="Mostrar apenas jogadores dentro do orçamento"
            />
            <span>Em orçamento ({formatPrice(budgetRemaining)})</span>
          </label>
        )}

        {hasActiveFilter && (
          <button className={styles.resetBtn} onClick={onReset} aria-label="Limpar filtros">
            Limpar filtros
          </button>
        )}
      </div>
    </div>
  )
}
